import type { AuthProvider } from 'react-admin';

import { sessionStorageTokenKey, sessionStorageUserDataKey } from '../../constants';
import { httpClient } from '../../httpClient';
import { ensureSdkReady, rolePermission } from '../../utils';

type FBLoginResponse = {
  authResponse: {
    accessToken: string;
    data_access_expiration_time: number;
    expiresIn: number;
    graphDomain: string;
    signedRequest: string;
    userID: string;
  };
  status: 'connected' | 'not_authorized' | 'unknown';
};

type FBMeResponse = {
  name: string;
  id: string;
};

const login = async () => {
  await ensureSdkReady();

  return new Promise<FBMeResponse>((resolve, reject) => {
    // @ts-expect-error no types
    window.FB.login((response: FBLoginResponse) => {
      if (response.status === 'connected') {
        sessionStorage.setItem(
          sessionStorageTokenKey,
          response.authResponse.accessToken,
        );

        // TODO: check the user role to make sure it is 'Admin' or 'Support'
        // @ts-expect-error no types
        return window.FB.api('/me', (res: FBMeResponse) => {
          return resolve(res);
        });
      }

      return reject();
    });
  });
};

const logout = async () => {
  const token = sessionStorage.getItem(sessionStorageTokenKey);
  sessionStorage.removeItem(sessionStorageTokenKey);
  sessionStorage.removeItem(sessionStorageUserDataKey);

  if (!token) {
    return Promise.resolve();
  }

  await ensureSdkReady();

  return new Promise<void>((resolve) => {
    // @ts-expect-error no types
    window.FB.logout(() => {
      resolve();
    });
  });
};

// TODO: define error type
const checkError = async (error: any) => {
  if (error.statusCode === 401 && error.error === 'Unauthorized') {
    await logout();
    return Promise.reject(error);
  }
};

const checkAuth = async () => {
  const token = sessionStorage.getItem(sessionStorageTokenKey);

  if (!token) {
    return Promise.reject();
  }

  await ensureSdkReady();

  return new Promise<void>((resolve, reject) => {
    // @ts-expect-error not typed
    window.FB.getLoginStatus(async (response) => {
      if (response.status !== 'connected') {
        await logout();
        return reject();
      }

      const podiyaResponse = await httpClient('/users/me');

      if (
        podiyaResponse.status !== 200 ||
        !rolePermission.auth(podiyaResponse.json.role)
      ) {
        await logout();
        return reject('Forbidden');
      }

      sessionStorage.setItem(
        sessionStorageUserDataKey,
        JSON.stringify(podiyaResponse.json),
      );

      return resolve();
    });
  });
};

const getIdentity = () => {
  // implemented in bridge since it's a common logic
  return Promise.reject('Not implemented.')
};

const getPermissions = async () => {
  // implemented in bridge, since it's a common logic
  return Promise.reject('Not implemented.')
};

const facebookAuthProvider: AuthProvider = {
  login,
  logout,
  checkError,
  checkAuth,
  getPermissions,
  getIdentity,
};

export default facebookAuthProvider;
